// @seventide/types — platform area: analytics (AN-1, AN-2). Re-exported by ../platform.ts.
// Analytics never carries PII, health text or amounts. Screen views carry ROUTE PATTERNS only
// (e.g. `money/gifts/:id`), never ids, query strings or names.
import { z } from 'zod';
import { IsoDate, IsoDateTime } from '../common';

// ---------------------------------------------------------------- screen views (AN-1 "feature usage by screen")

/** Same character rule as the server's analytics prop allow-list (`[a-z0-9_:/-]{1,64}`). */
export const SCREEN_SLUG_PATTERN = /^[a-z0-9_:/-]{1,64}$/;

const UUIDISH = /^[0-9a-f]{8}-?[0-9a-f]{4}/;
/** Segment that is a route word (`gifts`, `tax-years`), a param placeholder (`:id`) or empty (root). */
const SAFE_SEGMENT = /^(:[a-z][a-z0-9_]*|[a-z][a-z_-]*)$/;

/**
 * True when a screen slug is a route pattern: allowed characters only, and every segment is a lowercase word
 * without digits (so ids, dates, tax years and tokens can never slip through) or a `:param` placeholder.
 * `home` is used for the workspace index.
 */
export function isSafeScreenSlug(s: string): boolean {
  if (!SCREEN_SLUG_PATTERN.test(s)) return false;
  const segments = s.split('/');
  return segments.every((seg) => SAFE_SEGMENT.test(seg) && !UUIDISH.test(seg) && seg.length <= 32);
}

export const ScreenSlug = z
  .string()
  .refine(isSafeScreenSlug, { message: 'Screen must be a route pattern such as money/gifts/:id' });
export type ScreenSlug = z.infer<typeof ScreenSlug>;

/**
 * SR-29: the fixed vocabulary of screens — the web app's routes under /w/:wsId (apps/web/src/app/router.tsx and
 * the module route files), with every route parameter written `:id` and the workspace index as `home`. A screen
 * view is only recorded for one of these; anything else (e.g. a lowercase word that is really a name or a health
 * term) is dropped. apps/web's analytics test fails if this list and the router drift apart.
 */
export const SCREEN_ROUTES = [
  'home',
  'this-week',
  'money',
  'money/accounts',
  'money/accounts/:id',
  'money/transactions',
  'money/import',
  'money/gifts',
  'money/gifts/new',
  'money/gifts/:id',
  'money/gifts/:id/edit',
  'money/tax-years',
  'money/estate',
  'money/care',
  'money/care/contracts',
  'money/care/contracts/:id',
  'health',
  'health/diary',
  'health/diary/:id',
  'health/domains',
  'health/chc',
  'health/attendance-allowance',
  'records',
  'records/documents',
  'records/documents/upload',
  'records/documents/:id',
  'records/deprivation',
  'records/decisions',
  'records/decisions/new',
  'records/decisions/:id',
  'records/decisions/:id/edit',
  'records/attorney-report',
  'more',
  'more/security',
  'more/help',
  'more/help/specialist',
  'more/notifications',
  'more/rules',
  'more/explanations',
  'more/subscription',
  'more/data',
  'more/executor',
  'more/family',
  'more/principal',
  'more/activity',
] as const;
export type ScreenRoute = (typeof SCREEN_ROUTES)[number];
const SCREEN_ROUTE_SET: ReadonlySet<string> = new Set(SCREEN_ROUTES);

/** True when a slug is one of the app's screens (SR-29). */
export const isKnownScreen = (s: string): s is ScreenRoute => SCREEN_ROUTE_SET.has(s);

/**
 * POST /api/w/:id/platform/analytics/screen-views — batched, fire-and-forget. Malformed slugs are a 400; well-formed
 * slugs outside SCREEN_ROUTES are dropped (not recorded) and not counted in `accepted`.
 */
export const ScreenViewsRequest = z.object({ screens: z.array(ScreenSlug).min(1).max(20) });
export type ScreenViewsRequest = z.infer<typeof ScreenViewsRequest>;

export const ScreenViewsResponse = z.object({ accepted: z.number().int() });
export type ScreenViewsResponse = z.infer<typeof ScreenViewsResponse>;

// ---------------------------------------------------------------- admin dashboard (AN-2)

export const ANALYTICS_GRANULARITIES = ['month', 'week'] as const;
export const AnalyticsGranularity = z.enum(ANALYTICS_GRANULARITIES);
export type AnalyticsGranularity = z.infer<typeof AnalyticsGranularity>;

/** Modules whose first record counts as a "ledger entry" for activation (AN-2). */
export const ACTIVATION_MODULES = ['gifts', 'money', 'care'] as const;
/** Days after workspace creation within which the first ledger entry must be made to count as activated. */
export const ACTIVATION_WINDOW_DAYS = 7;

const queryBool = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((v) => v === true || v === 'true' || v === '1');

/** GET /api/admin/analytics/dashboard */
export const AnalyticsDashboardQuery = z
  .object({
    /** Inclusive start date (UK). Default: first day of the month, 5 months before this one (≈ 6 months). */
    from: IsoDate.optional(),
    /** Inclusive end date (UK). Default: today. */
    to: IsoDate.optional(),
    granularity: AnalyticsGranularity.default('month'),
    /** Leave out events marked `source=demo` by the seed. */
    excludeDemo: queryBool.optional().default(false),
  })
  .refine((v) => !v.from || !v.to || v.from <= v.to, { message: 'The start date must be before the end date.' });
export type AnalyticsDashboardQuery = z.infer<typeof AnalyticsDashboardQuery>;

const Rate = z.number().min(0).max(1).nullable();

export const AnalyticsPeriodRow = z.object({
  /** Period start: `yyyy-mm` (month) or `yyyy-mm-dd` (Monday of the week). */
  period: z.string(),
});

export const AnalyticsDashboard = z.object({
  range: z.object({ from: IsoDate, to: IsoDate, granularity: AnalyticsGranularity, excludeDemo: z.boolean() }),
  generatedAt: IsoDateTime,
  totals: z.object({
    workspacesCreated: z.number().int(),
    activated: z.number().int(),
    activationRate: Rate,
    trialsStarted: z.number().int(),
    trialsConverted: z.number().int(),
    trialConversionRate: Rate,
    subscriptionsStarted: z.number().int(),
    renewals: z.number().int(),
    cancellations: z.number().int(),
    exports: z.number().int(),
    familiesExporting: z.number().int(),
    activeFamilies: z.number().int(),
    exportsPerActiveFamily: z.number().nullable(),
    alertsFired: z.number().int(),
    alertsActed: z.number().int(),
    alertToActionRate: Rate,
    screenViews: z.number().int(),
  }),
  /** Workspaces created per period, and how many were activated within 7 days. */
  activation: z.array(
    AnalyticsPeriodRow.extend({ created: z.number().int(), activated: z.number().int(), rate: Rate }),
  ),
  /** Monthly cohorts by workspace creation month; `retained[n]` = active in month n after creation (n=0 is the creation month). */
  retention: z.array(
    z.object({ cohort: z.string(), size: z.number().int(), retained: z.array(z.number().int()) }),
  ),
  exports: z.object({
    byType: z.array(z.object({ type: z.string(), count: z.number().int(), families: z.number().int() })),
    byPeriod: z.array(AnalyticsPeriodRow.extend({ count: z.number().int(), families: z.number().int() })),
  }),
  alerts: z.array(
    AnalyticsPeriodRow.extend({ fired: z.number().int(), acted: z.number().int(), rate: Rate }),
  ),
  trialConversion: z.array(
    AnalyticsPeriodRow.extend({ trials: z.number().int(), converted: z.number().int(), rate: Rate }),
  ),
  churn: z.object({
    byReason: z.array(z.object({ reason: z.string(), count: z.number().int() })),
    byPeriod: z.array(AnalyticsPeriodRow.extend({ cancelled: z.number().int(), started: z.number().int() })),
  }),
  /** Distinct workspaces whose first record in each module happened in the range. */
  firstRecords: z.array(z.object({ module: z.string(), workspaces: z.number().int() })),
  /** Most viewed screens (route patterns only). */
  screens: z.array(z.object({ screen: z.string(), views: z.number().int(), families: z.number().int() })),
});
export type AnalyticsDashboard = z.infer<typeof AnalyticsDashboard>;
